/**
 * Overview — "Site Office" hero screen.
 *
 * Sections (unchanged from prior phase, restyled to the new visual system):
 *   1. Greeting + status sentence
 *   2. Next meeting ticket (or "Book a check-in" ghost)
 *   3. Pipeline stepper hero (valves + flowing connectors)
 *   4. Deliverables snapshot (ProgressRing, reused as-is, in a bracketed panel)
 *   5. Two primary CTAs
 *
 * Data: GET /api/console/overview
 * Query key: ['overview']
 *
 * LOGIC UNCHANGED — same hook, same destructuring, same nav callbacks,
 * same loading/error states. Only JSX/classNames changed to the new
 * .panel/.brk/.ticket/.pipe-wrap/.btnx/.stamp/.mlabel/.d-xl primitives and
 * --paper/--panel/--ink/--ink-soft/--line/--acc/--acc-ink tokens.
 */
import { useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api.js';
import Skeleton from '../components/Skeleton.jsx';
import ProgressRing from '../components/ProgressRing.jsx';
import { useNoteSheet } from '../context/NoteSheetContext.jsx';
import {
  CalendarDays,
  ArrowRight,
  Check,
  ChevronRight,
  AlertCircle,
  MessageCircle,
  KeyRound,
  Phone,
  CalendarCheck2,
  TrendingUp,
} from 'lucide-react';

// Stages where the ball is in the CLIENT'S court — the whole point of the
// action band is to make that unmissable on the landing screen, since the
// buttons that advance these stages otherwise live one (previously unrouted)
// tab away and clients silently stalled waiting on us.
const ACTION_STAGES = new Set(['paid_onboarding', 'access_pending', 'kickoff']);

// ── Client-facing timezone ────────────────────────────────────────────────────
// Clients are Texas businesses, so all meeting times render in Central Time
// regardless of the viewer's device tz, labelled "CT".
const CLIENT_TZ = 'America/Chicago';
function formatCT(utcIso) {
  return new Date(utcIso).toLocaleString(undefined, {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', timeZone: CLIENT_TZ,
  }) + ' CT';
}

// ── Stage → one-line sentence ─────────────────────────────────────────────────
const STAGE_SENTENCES = {
  prospect:            "We're in early discussions about your project.",
  contract_sent:       "Your service contract is on its way — check your email.",
  signed_unpaid:       "Contract signed — awaiting your first payment.",
  paid_onboarding:     "Payment received — gathering everything we need to start.",
  onboarding_complete: "Onboarding is complete — getting ready to build.",
  access_pending:      "Waiting on account access before we can begin building.",
  kickoff:             "Your kickoff call is scheduled — exciting things ahead.",
  building:            "Your website and automations are being built right now.",
  delivered:           "Your setup is delivered — reviewing the final details with you.",
  ongoing:             "You're live — our systems are running in the background.",
};

// ── Status pill (stamp) styles by meeting status ─────────────────────────────
const PILL_COLOR = {
  confirmed: 'var(--ok)',
  requested: 'var(--acc-ink)',
  declined:  'var(--ink-soft)',
  cancelled: 'var(--ink-soft)',
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatusPill({ status }) {
  return (
    <span className="stamp flat" style={{ color: PILL_COLOR[status] || PILL_COLOR.requested }}>
      {status}
    </span>
  );
}

/**
 * PipeStepper — horizontal pipeline diagram, scrollable overflow.
 * Current step: amber valve node + spinning valve-ring + pulsing halo.
 * Done steps: amber-filled valve + Check icon; connector to it carries the
 * flowing diagonal-stripe texture (`.pipe-flow`) suggesting throughput.
 * Upcoming: muted, unlit node, plain connector.
 */
function PipeStepper({ stageList }) {
  const currentIdx = stageList.findIndex((s) => s.status === 'current');
  const currentStage = currentIdx >= 0 ? stageList[currentIdx] : null;
  const nextStage =
    currentIdx >= 0 && currentIdx < stageList.length - 1
      ? stageList[currentIdx + 1]
      : null;

  const scrollRef  = useRef(null);
  const currentRef = useRef(null);

  // On mount: scroll the track so the current step is in view (centred).
  useEffect(() => {
    const track = scrollRef.current;
    const node  = currentRef.current;
    if (!track || !node) return;
    const nodeLeft = node.offsetLeft;
    const nodeW    = node.offsetWidth;
    const trackW   = track.clientWidth;
    track.scrollLeft = nodeLeft - (trackW / 2) + (nodeW / 2);
  }, [stageList]);

  return (
    <div>
      <div ref={scrollRef} role="list" aria-label="Project stages" className="pipe-wrap">
        <style>{`div[aria-label="Project stages"]::-webkit-scrollbar { display: none; }`}</style>

        <div style={{ display: 'flex', alignItems: 'flex-start', minWidth: 'max-content', paddingInline: '10px', paddingTop: '10px' }}>
          {stageList.map((stage, i) => {
            const isDone    = stage.status === 'done';
            const isCurrent = stage.status === 'current';
            const isLast    = i === stageList.length - 1;

            return (
              <div key={stage.key} role="listitem" style={{ display: 'flex', alignItems: 'flex-start' }}>
                {/* Valve node + label */}
                <div
                  ref={isCurrent ? currentRef : null}
                  style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '64px', flexShrink: 0 }}
                >
                  <div
                    aria-current={isCurrent ? 'step' : undefined}
                    aria-label={`${stage.label} (${stage.status})`}
                    style={{
                      position: 'relative',
                      width: '22px',
                      height: '22px',
                      borderRadius: '50%',
                      background: isDone || isCurrent ? 'var(--acc)' : 'var(--panel-2)',
                      border: !isDone && !isCurrent ? '2px solid var(--line-strong)' : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {isDone && <Check size={12} color="var(--paper)" strokeWidth={3} aria-hidden="true" />}
                    {isCurrent && (
                      <>
                        <div aria-hidden="true" style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--paper)' }} />
                        {/* Pulsing halo — "You are here" */}
                        <div
                          aria-hidden="true"
                          className="pulse-ring"
                          style={{ position: 'absolute', inset: '-6px', borderRadius: '50%', border: '2px solid var(--acc)', pointerEvents: 'none' }}
                        />
                        {/* Spinning valve-wheel crosshairs */}
                        <svg aria-hidden="true" className="valve-ring" width="32" height="32" viewBox="0 0 32 32" style={{ position: 'absolute', inset: '-5px', pointerEvents: 'none' }}>
                          <g stroke="var(--acc-ink)" strokeWidth="1.5">
                            <line x1="16" y1="1" x2="16" y2="7" />
                            <line x1="16" y1="25" x2="16" y2="31" />
                            <line x1="1" y1="16" x2="7" y2="16" />
                            <line x1="25" y1="16" x2="31" y2="16" />
                          </g>
                        </svg>
                      </>
                    )}
                  </div>

                  <span
                    title={stage.label}
                    className="mono"
                    style={{
                      marginTop: '9px',
                      fontSize: '10px',
                      lineHeight: 1.3,
                      textAlign: 'center',
                      fontWeight: isCurrent ? 600 : 400,
                      color: isCurrent ? 'var(--acc-ink)' : isDone ? 'var(--ink)' : 'var(--ink-soft)',
                      width: '64px',
                      overflowWrap: 'break-word',
                      hyphens: 'auto',
                    }}
                  >
                    {stage.label}
                  </span>
                </div>

                {/* Pipe connector segment */}
                {!isLast && (
                  <div
                    aria-hidden="true"
                    className={isDone ? 'pipe-flow' : ''}
                    style={{
                      height: '6px',
                      width: '18px',
                      marginTop: '8px',
                      flexShrink: 0,
                      borderRadius: '1px',
                      background: isDone ? 'var(--acc)' : 'var(--panel-2)',
                      border: isDone ? 'none' : '1px solid var(--line)',
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* "Now: … / Next: …" row */}
      {(currentStage || nextStage) && (
        <div style={{ marginTop: '14px', display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', lineHeight: 1.4 }}>
          {currentStage && (
            <span>
              <span className="mlabel" style={{ marginRight: '2px' }}>Now:</span>{' '}
              <span style={{ color: 'var(--acc-ink)', fontWeight: 600 }}>{currentStage.label}</span>
            </span>
          )}
          {nextStage && (
            <span>
              <span className="mlabel" style={{ marginRight: '2px' }}>Next:</span>{' '}
              <span style={{ color: 'var(--ink)' }}>{nextStage.label}</span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ── This-week value band ──────────────────────────────────────────────────────
// The antidote to "they forgot it's working". For a live client, this is the
// first thing on the home screen: proof, in their own numbers, that the AI
// earned its keep this week. Only shown when there's real activity — an empty
// band would just remind them of a quiet week.
function ValueBand({ weekly, navigate }) {
  const stats = [
    { icon: Phone, value: weekly.calls_answered, label: weekly.calls_answered === 1 ? 'call answered' : 'calls answered' },
    { icon: CalendarCheck2, value: weekly.jobs_booked, label: weekly.jobs_booked === 1 ? 'job booked' : 'jobs booked' },
  ];
  if (weekly.est_revenue) {
    stats.push({ icon: TrendingUp, value: `$${Number(weekly.est_revenue).toLocaleString('en-US')}`, label: 'est. work booked' });
  }
  return (
    <div className="panel" style={{ padding: 18, position: 'relative' }}>
      <span className="brk brk-tl" aria-hidden="true" />
      <span className="brk brk-br" aria-hidden="true" />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <h2 className="mlabel" style={{ margin: 0 }}>Your week</h2>
        <button
          type="button"
          onClick={() => navigate('/calls')}
          style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'transparent', border: 'none', color: 'var(--acc-ink)', fontSize: 12, fontFamily: 'var(--font-mono)', fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', cursor: 'pointer', padding: 0 }}
        >
          Listen back <ArrowRight size={13} aria-hidden="true" />
        </button>
      </div>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {stats.map((s) => (
          <div key={s.label} style={{ flex: '1 1 90px', minWidth: 90, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <s.icon size={15} color="var(--acc)" strokeWidth={2.25} aria-hidden="true" />
              <span className="d-xl" style={{ fontSize: 26, color: 'var(--ink)', lineHeight: 1 }}>{s.value}</span>
            </div>
            <span className="mono" style={{ fontSize: 11, color: 'var(--ink-soft)', letterSpacing: '0.04em' }}>{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Action band ───────────────────────────────────────────────────────────────
// Rendered above the fold when the client owes us an action. Turns the passive
// "Waiting on account access…" sentence into a workable to-do with the actual
// button, so onboarding self-advances instead of stalling.
function ActionBand({ stage, accessItems, onConfirmAccess, confirming, confirmError, navigate }) {
  let title;
  let body;
  let action;

  if (stage === 'paid_onboarding') {
    title = 'Complete your onboarding form';
    body = 'A few quick details so we can start building — check your email for your private form link. It takes about 3 minutes.';
    action = (
      <button type="button" className="btnx quiet" onClick={() => navigate('/project')}>
        See what's next <ArrowRight size={14} aria-hidden="true" />
      </button>
    );
  } else if (stage === 'access_pending') {
    title = 'Share access so we can start building';
    const top = (accessItems || []).slice(0, 3);
    body = top.length
      ? 'We need a few access points before the build can begin:'
      : 'We need a few account access points before the build can begin. Once you\'ve shared them, tap below.';
    action = (
      <>
        <button type="button" className="btnx primary" onClick={onConfirmAccess} disabled={confirming}>
          {confirming ? 'Confirming…' : "I've shared access"}
        </button>
        <button type="button" className="btnx quiet" onClick={() => navigate('/project')}>
          See full checklist <ArrowRight size={14} aria-hidden="true" />
        </button>
      </>
    );
    if (top.length) {
      body = (
        <>
          <p style={{ margin: '0 0 10px' }}>We need a few access points before the build can begin:</p>
          <ul style={{ margin: '0 0 4px 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {top.map((it) => (
              <li key={it.title} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: 'var(--ink)' }}>
                <span aria-hidden="true" style={{ color: 'var(--acc)', flexShrink: 0 }}>•</span>
                <span>{it.title}</span>
              </li>
            ))}
          </ul>
        </>
      );
    }
  } else if (stage === 'kickoff') {
    title = 'Book your kickoff call';
    body = 'Lock in a short kickoff so we can confirm scope and get your build moving.';
    action = (
      <button type="button" className="btnx primary" onClick={() => navigate('/meetings')}>
        <CalendarDays size={15} aria-hidden="true" /> Book kickoff
      </button>
    );
  } else {
    return null;
  }

  return (
    <div
      className="panel"
      style={{ padding: 18, position: 'relative', borderColor: 'var(--acc)', background: 'var(--acc-soft)' }}
    >
      <span className="brk brk-tl" aria-hidden="true" />
      <span className="brk brk-br" aria-hidden="true" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 8px' }}>
        <KeyRound size={15} color="var(--acc-ink)" strokeWidth={2.25} aria-hidden="true" />
        <span className="mlabel" style={{ color: 'var(--acc-ink)', margin: 0 }}>Action needed</span>
      </div>
      <p style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 700, color: 'var(--ink)' }}>{title}</p>
      <div style={{ margin: '0 0 14px', fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.5 }}>{body}</div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{action}</div>
      {confirmError && (
        <p style={{ margin: '10px 0 0', fontSize: 12, color: 'var(--bad)' }}>Couldn't confirm — please try again.</p>
      )}
    </div>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────
function OverviewSkeleton() {
  return (
    <div style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <div>
        <Skeleton style={{ height: 30, width: '72%', marginBottom: '8px' }} />
        <Skeleton style={{ height: 16, width: '90%' }} />
      </div>
      <Skeleton style={{ height: 74, borderRadius: 0 }} />
      <div className="panel" style={{ padding: '16px' }}>
        <Skeleton style={{ height: 12, width: '28%', marginBottom: '16px' }} />
        <Skeleton style={{ height: 56 }} />
        <Skeleton style={{ height: 13, width: '55%', marginTop: '14px' }} />
      </div>
      <div className="panel" style={{ padding: '16px' }}>
        <Skeleton style={{ height: 12, width: '35%', marginBottom: '16px' }} />
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <Skeleton style={{ height: 18 }} />
            <Skeleton style={{ height: 18 }} />
            <Skeleton style={{ height: 18, width: '75%' }} />
          </div>
          <Skeleton style={{ width: '88px', height: '88px', borderRadius: '50%', flexShrink: 0 }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: '12px' }}>
        <Skeleton style={{ height: 50, flex: 1, borderRadius: 0 }} />
        <Skeleton style={{ height: 50, flex: 1, borderRadius: 0 }} />
      </div>
    </div>
  );
}

// ── Error state ───────────────────────────────────────────────────────────────
function OverviewError({ error, refetch }) {
  return (
    <div style={{ padding: '20px 16px', paddingTop: '64px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '16px' }}>
      <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'var(--panel-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <AlertCircle size={24} color="var(--ink-soft)" strokeWidth={1.5} aria-hidden="true" />
      </div>
      <div>
        <p className="d-xl" style={{ margin: '0 0 6px', fontSize: '20px', color: 'var(--ink)' }}>
          Couldn't load overview
        </p>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: '14px', lineHeight: 1.5 }}>
          {error?.message || 'Something went wrong. Tap below to try again.'}
        </p>
      </div>
      <button type="button" onClick={() => refetch()} className="btnx primary">
        Try again
      </button>
    </div>
  );
}

// ── Main screen ───────────────────────────────────────────────────────────────
export default function Overview() {
  const navigate = useNavigate();
  const { open: openSheet } = useNoteSheet();
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['overview'],
    queryFn: () => apiFetch('/api/console/overview'),
  });

  // Lifecycle detail (access items) — only needed to power the action band.
  // Shares the ['lifecycle'] cache with the Project screen, so no extra cost
  // in the common case.
  const { data: lifecycle } = useQuery({
    queryKey: ['lifecycle'],
    queryFn: () => apiFetch('/api/console/lifecycle'),
    enabled: ACTION_STAGES.has(data?.stage),
  });

  const confirmAccess = useMutation({
    mutationFn: () => apiFetch('/api/console/lifecycle/confirm-access', { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lifecycle'] });
      queryClient.invalidateQueries({ queryKey: ['overview'] });
    },
  });

  if (isLoading) return <OverviewSkeleton />;
  if (error) return <OverviewError error={error} refetch={refetch} />;

  const {
    stage,
    stage_list:         stageList    = [],
    deliverable_counts: counts       = {},
    deliverable_total:  total        = 0,
    next_meeting:       meeting      = null,
    business_name:      biz          = '',
    owner_name:         ownerName    = '',
    preview_items:      previewItems = [],
    this_week:          weekly       = null,
  } = data;

  // Greet by the owner's first name when we have it — warmer than the business
  // name. Falls back to the business name, then nothing.
  const firstName = (ownerName || '').trim().split(/\s+/)[0] || '';
  const greetName = firstName || biz;
  const sentence  = STAGE_SENTENCES[stage] || 'Your project is underway.';
  const showValueBand = weekly && weekly.calls_answered > 0;
  const delivered = counts.delivered ?? 0;
  const inProg    = counts.in_progress ?? 0;
  const upcoming  = counts.upcoming ?? 0;

  return (
    <div className="gridbg" style={{ padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* ── 1. Greeting ──────────────────────────────────────────────────── */}
      <div>
        <h1 className="d-xl" style={{ fontSize: '30px', color: 'var(--ink)', margin: '0 0 6px' }}>
          Welcome back{greetName ? `, ${greetName}` : ''}
        </h1>
        <p style={{ margin: 0, color: 'var(--ink-soft)', fontSize: '15px', lineHeight: 1.55 }}>
          {sentence}
        </p>
      </div>

      {/* ── 1a. This-week value band — proof of value for live clients ────── */}
      {showValueBand && <ValueBand weekly={weekly} navigate={navigate} />}

      {/* ── 1b. Action band — only when the client owes us a step ────────── */}
      {ACTION_STAGES.has(stage) && (
        <ActionBand
          stage={stage}
          accessItems={lifecycle?.access_items}
          onConfirmAccess={() => confirmAccess.mutate()}
          confirming={confirmAccess.isPending}
          confirmError={confirmAccess.isError}
          navigate={navigate}
        />
      )}

      {/* ── 2. Next meeting ticket ───────────────────────────────────────── */}
      {meeting ? (
        <button
          type="button"
          onClick={() => navigate('/meetings')}
          aria-label={`Next meeting on ${new Date(meeting.start_at).toLocaleString()}, status: ${meeting.status}. Tap to view meetings.`}
          className="ticket hoverable"
          style={{ display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer', padding: 0, background: 'var(--panel)', font: 'inherit' }}
        >
          <span className="notch top" aria-hidden="true" />
          <span className="notch bot" aria-hidden="true" />
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '16px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
              <div style={{ width: '34px', flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                <CalendarDays size={19} color="var(--acc)" strokeWidth={2} aria-hidden="true" />
              </div>
              <div style={{ minWidth: 0 }}>
                <p className="mlabel" style={{ margin: '0 0 3px' }}>Next check-in</p>
                <p style={{ margin: 0, fontWeight: 700, fontSize: '14px', color: 'var(--ink)', lineHeight: 1.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {formatCT(meeting.start_at)}
                </p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
              <StatusPill status={meeting.status} />
              <ChevronRight size={16} color="var(--ink-soft)" aria-hidden="true" />
            </div>
          </div>
        </button>
      ) : (
        /* No meeting — "Book a check-in" ghost link */
        <button
          type="button"
          onClick={() => navigate('/meetings')}
          aria-label="Book a check-in meeting"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            padding: '13px 16px',
            width: '100%',
            boxSizing: 'border-box',
            background: 'transparent',
            border: '1.5px dashed var(--line-strong)',
            cursor: 'pointer',
            color: 'var(--ink-soft)',
            fontFamily: 'var(--font-mono)',
            fontSize: '12px',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            transition: 'color 200ms ease, border-color 200ms ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--ink)'; e.currentTarget.style.borderColor = 'var(--acc)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--ink-soft)'; e.currentTarget.style.borderColor = 'var(--line-strong)'; }}
        >
          <CalendarDays size={16} aria-hidden="true" />
          Book a check-in
        </button>
      )}

      {/* ── 3. Pipeline stepper hero ─────────────────────────────────────── */}
      <div className="panel" style={{ padding: '16px', position: 'relative', overflow: 'hidden' }}>
        <span className="brk brk-tl" aria-hidden="true" />
        <span className="brk brk-br" aria-hidden="true" />

        {/* Subtle amber radial glow behind the hero */}
        <div
          aria-hidden="true"
          style={{
            position: 'absolute', top: '-40px', right: '-40px', width: '200px', height: '200px',
            background: 'radial-gradient(circle, var(--acc-soft) 0%, transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <h2 className="mlabel" style={{ margin: '0 0 16px' }}>Project Stage</h2>
        <PipeStepper stageList={stageList} />
      </div>

      {/* ── 4. Deliverables snapshot ──────────────────────────────────────── */}
      <div className="panel" style={{ padding: '16px', position: 'relative' }}>
        <span className="brk brk-tl" aria-hidden="true" />
        <span className="brk brk-br" aria-hidden="true" />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <h2 className="mlabel" style={{ margin: 0 }}>Deliverables</h2>
          <button
            type="button"
            onClick={() => navigate('/deliverables')}
            aria-label="See all deliverables"
            style={{
              display: 'flex', alignItems: 'center', gap: '4px',
              background: 'transparent', border: 'none', color: 'var(--acc-ink)',
              fontSize: '12px', fontFamily: 'var(--font-mono)', fontWeight: 600,
              letterSpacing: '0.04em', textTransform: 'uppercase',
              cursor: 'pointer', padding: 0,
            }}
          >
            See all
            <ArrowRight size={13} aria-hidden="true" />
          </button>
        </div>

        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          {/* Left: item preview + summary line */}
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '9px' }}>
            {previewItems.length > 0 ? (
              previewItems.slice(0, 3).map((item) => (
                <div
                  key={item.key}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px',
                    color: item.group === 'upcoming' ? 'var(--ink-soft)' : 'var(--ink)', lineHeight: 1.3,
                  }}
                >
                  <div
                    aria-hidden="true"
                    style={{ width: '5px', height: '5px', borderRadius: '50%', flexShrink: 0, background: item.group === 'in_progress' ? 'var(--acc)' : 'var(--ink-soft)' }}
                  />
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.label}</span>
                </div>
              ))
            ) : (
              <>
                {inProg > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--ink)' }}>
                    <div aria-hidden="true" style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--acc)', flexShrink: 0 }} />
                    {inProg} in progress
                  </div>
                )}
                {upcoming > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--ink-soft)' }}>
                    <div aria-hidden="true" style={{ width: '5px', height: '5px', borderRadius: '50%', background: 'var(--ink-soft)', flexShrink: 0 }} />
                    {upcoming} upcoming
                  </div>
                )}
              </>
            )}

            <div style={{ marginTop: '4px', paddingTop: '10px', borderTop: '1px solid var(--line)' }}>
              <span className="mono" style={{ fontSize: '12px', color: 'var(--ink-soft)' }}>
                {delivered} of {total} delivered
              </span>
            </div>
          </div>

          {/* Right: progress ring — reused unchanged, no gauge reimplementation */}
          <ProgressRing delivered={delivered} total={total} size={88} />
        </div>
      </div>

      {/* ── 5. CTAs ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '12px' }}>
        <button type="button" onClick={() => navigate('/meetings')} className="btnx primary" style={{ flex: 1 }}>
          <CalendarDays size={15} aria-hidden="true" />
          <span>Request a meeting</span>
        </button>

        <button
          type="button"
          onClick={openSheet}
          aria-label="Ask a quick question"
          className="btnx quiet"
          style={{ flex: 1 }}
        >
          <MessageCircle size={15} aria-hidden="true" />
          <span>Ask a question</span>
        </button>
      </div>
    </div>
  );
}
